"use client";

import { useEffect } from "react";

export default function Error({ error, reset }) {
  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <main className="min-h-screen bg-[#F2F2F2] flex flex-col">
      {/* ── Hero Header — mirrors blog page header ── */}
      <header className="relative overflow-hidden bg-[#283245] text-white">
        {/* Decorative circles */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 -right-16 w-72 h-72 rounded-full"
          style={{ background: "rgba(149,115,247,0.12)" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-10 -left-10 w-48 h-48 rounded-full"
          style={{ background: "rgba(149,115,247,0.08)" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 pt-6 pb-10">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-1.5 text-sm text-white/60 flex-wrap">
              <li>
                <a
                  href="/"
                  className="hover:text-white transition-colors duration-200 flex items-center gap-1"
                >
                  <svg
                    className="w-3.5 h-3.5 shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7A1 1 0 003 11h1v7a1 1 0 001 1h4v-5h2v5h4a1 1 0 001-1v-7h1a1 1 0 00.707-1.707l-7-7z" />
                  </svg>
                  Home
                </a>
              </li>
              <li aria-hidden className="text-white/30">
                /
              </li>
              <li>
                <span className="text-white font-medium" aria-current="page">
                  Error
                </span>
              </li>
            </ol>
          </nav>

          {/* Title */}
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-[#9573F7] font-semibold mb-2">
              We'll be right back
            </p>
            <h1 className="text-4xl sm:text-5xl font-light text-white leading-tight">
              Temporarily Unavailable
            </h1>
            <p className="text-white/60 mt-3 text-base sm:text-lg max-w-lg mx-auto">
              This page isn't available right now - please check back shortly
            </p>

            {/* Decorative divider */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <span className="h-px w-12 bg-[#9573F7]/40" />
              <span className="w-1.5 h-1.5 rounded-full bg-[#9573F7]" />
              <span className="h-px w-12 bg-[#9573F7]/40" />
            </div>
          </div>
        </div>
      </header>

      {/* ── Card ── */}
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
            {/* Top accent bar */}
            <div className="h-1.5 w-full bg-gradient-to-r from-[#9573F7] via-[#b89dff] to-[#283245]" />

            <div className="px-8 py-10 text-center">
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-[#9573F7]/10 flex items-center justify-center">
                  <svg
                    className="w-7 h-7 text-[#9573F7]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l5.653-4.655m5.8-2.613.666-2.337a1.5 1.5 0 0 0-.994-1.86l-1.226-.41a1.5 1.5 0 0 1-.994-1.86l.41-1.226a1.5 1.5 0 0 0-1.86-.994l-2.337.666A4.5 4.5 0 0 0 6.75 9v.75"
                    />
                  </svg>
                </div>
              </div>

              {/* Message */}
              <p className="text-gray-500 text-sm leading-relaxed">
                Our team is on it. Everything will be back up and running for
                you very soon - thanks for your patience!
              </p>

              {/* Dev-only error detail */}
              {error?.message &&
                process.env.NEXT_PUBLIC_ENV === "development" && (
                  <p className="text-xs text-gray-400 bg-gray-50 border border-gray-100 rounded-lg px-4 py-2 mt-4 font-mono text-left truncate">
                    {error.message}
                  </p>
                )}

              {/* Divider */}
              <hr className="border-gray-100 my-7" />

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={reset}
                  className="flex items-center justify-center gap-2 bg-[#9573F7] hover:bg-[#7a5bd1] text-white text-sm font-medium px-6 py-2.5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#9573F7] focus:ring-offset-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                    />
                  </svg>
                  Try again
                </button>

                <a
                  href="/"
                  className="flex items-center justify-center gap-2 bg-white border border-gray-200 hover:border-[#9573F7] text-[#283245] hover:text-[#9573F7] text-sm font-medium px-6 py-2.5 rounded-full transition-colors duration-200"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                    />
                  </svg>
                  Go home
                </a>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-gray-400 mt-5">
            If this keeps happening, please contact support.
          </p>
        </div>
      </div>
    </main>
  );
}
